import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { ROLES } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  FieldGroup,
  Input,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { formatDate, todayISO } from '@/lib/format';
import { useAuth } from '@/store/auth';
import type { ClassRoomSummary, EducationPlan } from '@/types';

/**
 * 교육계획안(§5.19)
 *
 * 학부모는 공개된 계획안만 본다(서버가 거른다).
 * 보육일지는 v1.19에서 양식 기반 독립 화면(/care-journal)으로 분리됐다.
 */
export function CurriculumPage() {
  const membership = useAuth((s) => s.membership);
  const navigate = useNavigate();
  const isParent = membership?.role === ROLES.PARENT;

  return (
    <Screen>
      <AppBar title={isParent ? '교육계획안' : '교육계획'} back />

      {!isParent && (
        <div className="px-4 pt-3">
          <div className="flex gap-2">
            <Chip tone="primary" selected>
              계획안
            </Chip>
            <Chip tone="neutral" onClick={() => navigate('/care-journal')}>
              보육일지
            </Chip>
          </div>
        </div>
      )}

      <PlansTab isParent={isParent} />
    </Screen>
  );
}

const PERIOD_LABELS = { WEEK: '주간', MONTH: '월간' } as const;

function PlansTab({ isParent }: { isParent: boolean }) {
  const [composing, setComposing] = useState(false);
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['education-plans'],
    queryFn: () => apiData<EducationPlan[]>('/education-plans'),
  });

  const plans = data ?? [];

  return (
    <div className="flex flex-col gap-3 p-4">
      {!isParent && !composing && (
        <Button variant="outline" icon={<Plus size={16} />} onClick={() => setComposing(true)}>
          계획안 등록
        </Button>
      )}
      {composing && <PlanForm onClose={() => setComposing(false)} />}

      {isPending && <Spinner />}
      {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

      {!isPending && plans.length === 0 && !composing && (
        <EmptyState
          title="등록된 교육계획안이 없어요"
          body={isParent ? '원에서 공개하면 여기에서 볼 수 있어요.' : '주간·월간 계획안을 남겨 보세요.'}
        />
      )}

      {plans.map((plan) => (
        <Card key={plan.id} as="div">
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
            <Chip tone="primary">{PERIOD_LABELS[plan.periodType]}</Chip>
            <Chip tone="neutral">{plan.classRoom.name}</Chip>
            {!isParent && (
              <Chip tone={plan.isPublic ? 'accent' : 'neutral'}>
                {plan.isPublic ? '학부모 공개' : '비공개'}
              </Chip>
            )}
            <span className="ml-auto text-sm text-ink-sub">
              {formatDate(plan.startDate)} – {formatDate(plan.endDate)}
            </span>
          </div>
          {plan.topic && <p className="font-semibold">{plan.topic}</p>}
          {plan.goals && <p className="mt-1 text-base text-ink-sub">{plan.goals}</p>}
          {plan.activities && <p className="mt-1 whitespace-pre-wrap text-base">{plan.activities}</p>}
        </Card>
      ))}
    </div>
  );
}

function PlanForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const rooms = useQuery({
    queryKey: ['classrooms'],
    queryFn: () => apiData<ClassRoomSummary[]>('/classrooms'),
  });

  const [classRoomId, setClassRoomId] = useState('');
  const [periodType, setPeriodType] = useState<'WEEK' | 'MONTH'>('WEEK');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [topic, setTopic] = useState('');
  const [goals, setGoals] = useState('');
  const [activities, setActivities] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveRoomId = classRoomId || rooms.data?.[0]?.id || '';

  const submit = useMutation({
    mutationFn: () =>
      apiData('/education-plans', {
        method: 'POST',
        body: {
          classRoomId: effectiveRoomId,
          periodType,
          startDate,
          endDate,
          topic: topic.trim() || undefined,
          goals: goals.trim() || undefined,
          activities: activities.trim() || undefined,
          isPublic,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['education-plans'] });
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '등록하지 못했어요.'),
  });

  return (
    <Card>
      <p className="mb-3 font-bold">계획안 등록</p>

      <FieldGroup label="반" required>
        <div className="flex flex-wrap gap-1.5">
          {rooms.data?.map((room) => (
            <Chip
              key={room.id}
              tone={room.id === effectiveRoomId ? 'primary' : 'neutral'}
              selected={room.id === effectiveRoomId}
              onClick={() => setClassRoomId(room.id)}
            >
              {room.name}
            </Chip>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="구분">
        <div className="flex gap-1.5">
          {(['WEEK', 'MONTH'] as const).map((period) => (
            <Chip
              key={period}
              tone={period === periodType ? 'primary' : 'neutral'}
              selected={period === periodType}
              onClick={() => setPeriodType(period)}
            >
              {PERIOD_LABELS[period]}
            </Chip>
          ))}
        </div>
      </FieldGroup>

      <div className="mb-3 grid grid-cols-2 gap-2">
        <Field label="시작일">
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="종료일">
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
      </div>

      <Field label="주제" required>
        <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="예: 여름 물놀이" />
      </Field>

      <Field label="목표">
        <Textarea rows={2} value={goals} onChange={(e) => setGoals(e.target.value)} />
      </Field>

      <Field label="주요 활동">
        <Textarea rows={3} value={activities} onChange={(e) => setActivities(e.target.value)} />
      </Field>

      <label className="mb-3 flex items-center gap-2 text-base">
        <input
          type="checkbox"
          checked={isPublic}
          onChange={(e) => setIsPublic(e.target.checked)}
          className="h-5 w-5 accent-[color:var(--color-primary)]"
        />
        학부모에게 공개하기
      </label>

      {error && <p className="mb-2 text-sm text-notice">{error}</p>}

      <div className="flex gap-2">
        <Button variant="outline" block onClick={onClose}>
          취소
        </Button>
        <Button block disabled={!effectiveRoomId || !topic.trim()} loading={submit.isPending} onClick={() => submit.mutate()}>
          등록
        </Button>
      </div>
    </Card>
  );
}
